import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Allega l'header X-Venue-Id su tutte le request (eccetto chiamate esplicitamente
  // marcate con `config._skipVenue = true`).
  if (!config._skipVenue) {
    const venueId = localStorage.getItem('active_venue_id');
    if (venueId) {
      config.headers['X-Venue-Id'] = venueId;
    }
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Info app (versione in uso + storico rilasci)
export const infoApi = {
  getVersione: async () => (await api.get('/info/versione', { _skipVenue: true })).data,
};

// Auth API
export const authApi = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  register: async (email, password, nome, ruolo = 'operatore_cassa') => {
    const response = await api.post('/auth/register', { email, password, nome, ruolo });
    return response.data;
  },
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  changePassword: async (currentPassword, newPassword) => {
    const response = await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },
};

// Locali API (admin / super_admin only)
export const localiApi = {
  getAll: async (includeInattivi = true) => {
    const response = await api.get(`/locali?include_inattivi=${includeInattivi}`);
    return response.data;
  },
  getMine: async () => {
    const response = await api.get('/locali/me');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/locali/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/locali', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/locali/${id}`, data);
    return response.data;
  },
  deactivate: async (id) => {
    const response = await api.post(`/locali/${id}/deactivate`);
    return response.data;
  },
  activate: async (id) => {
    const response = await api.post(`/locali/${id}/activate`);
    return response.data;
  },
};

// Group Dashboard API (super_admin only) — cross-venue, NO X-Venue-Id header
export const groupDashboardApi = {
  getStats: async ({ dataInizio, dataFine, venueIds = [], includeInattivi = false }) => {
    const params = new URLSearchParams();
    params.set('data_inizio', dataInizio);
    params.set('data_fine', dataFine);
    if (Array.isArray(venueIds)) {
      venueIds.forEach((id) => params.append('venue_ids', id));
    }
    params.set('include_inattivi', String(includeInattivi));
    const response = await api.get(`/group-dashboard/stats?${params.toString()}`, { _skipVenue: true });
    return response.data;
  },
  exportXlsxUrl: ({ dataInizio, dataFine, venueIds = [], includeInattivi = false }) => {
    const params = new URLSearchParams();
    params.set('data_inizio', dataInizio);
    params.set('data_fine', dataFine);
    if (Array.isArray(venueIds)) {
      venueIds.forEach((id) => params.append('venue_ids', id));
    }
    params.set('include_inattivi', String(includeInattivi));
    return `/api/group-dashboard/export.xlsx?${params.toString()}`;
  },
  downloadXlsx: async ({ dataInizio, dataFine, venueIds = [], includeInattivi = false }) => {
    const params = new URLSearchParams();
    params.set('data_inizio', dataInizio);
    params.set('data_fine', dataFine);
    if (Array.isArray(venueIds)) {
      venueIds.forEach((id) => params.append('venue_ids', id));
    }
    params.set('include_inattivi', String(includeInattivi));
    const response = await api.get(`/group-dashboard/export.xlsx?${params.toString()}`, {
      responseType: 'blob',
      _skipVenue: true,
    });
    return response;
  },
};

// Chiusure API
export const chiusureApi = {
  getAll: async (skip = 0, limit = 100, dataInizio = null, dataFine = null) => {
    let url = `/chiusure?skip=${skip}&limit=${limit}`;
    if (dataInizio) url += `&data_inizio=${dataInizio}`;
    if (dataFine) url += `&data_fine=${dataFine}`;
    const response = await api.get(url);
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/chiusure/${id}`);
    return response.data;
  },
  getParzialeOggi: async () => {
    const response = await api.get('/chiusure/parziali/today');
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/chiusure', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/chiusure/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/chiusure/${id}`);
    return response.data;
  },
  exportCsv: async () => {
    const response = await api.get('/chiusure/export/csv', {
      responseType: 'blob',
    });
    return response.data;
  },
  exportXlsx: async (dataInizio = null, dataFine = null) => {
    let url = '/chiusure/export/xlsx';
    const params = [];
    if (dataInizio) params.push(`data_inizio=${dataInizio}`);
    if (dataFine) params.push(`data_fine=${dataFine}`);
    if (params.length > 0) url += '?' + params.join('&');
    
    const response = await api.get(url, { responseType: 'blob' });
    return response.data;
  },
  downloadTemplate: async () => {
    const response = await api.get('/chiusure/template/xlsx', { responseType: 'blob' });
    return response.data;
  },
  importPreview: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/chiusure/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  importConfirm: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/chiusure/import/confirm', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  listBackups: async () => {
    const response = await api.get('/chiusure/import/backups');
    return response.data;
  },
  restoreBackup: async (backupId) => {
    const response = await api.post(`/chiusure/import/restore/${backupId}`);
    return response.data;
  },
};

// Dashboard API
export const dashboardApi = {
  getStats: async (dataInizio, dataFine, confronta = false) => {
    const response = await api.get(
      `/dashboard/stats?data_inizio=${dataInizio}&data_fine=${dataFine}&confronta=${confronta}`
    );
    return response.data;
  },
};

// Bilancio API
export const bilancioApi = {
  getBilancio: async (anno, mese) => {
    const response = await api.get(`/bilancio/${anno}/${mese}`);
    return response.data;
  },
  saveSpese: async (anno, mese, spese) => {
    const response = await api.post(`/bilancio/${anno}/${mese}`, { anno, mese, spese });
    return response.data;
  },
  getAnniDisponibili: async () => {
    const response = await api.get('/bilancio/anni-disponibili');
    return response.data;
  },
  getSettori: async () => {
    const response = await api.get('/settori-spesa');
    return response.data;
  },
  createSettore: async (data) => {
    const response = await api.post('/settori-spesa', data);
    return response.data;
  },
  updateSettore: async (settoreId, data) => {
    const response = await api.put(`/settori-spesa/${settoreId}`, data);
    return response.data;
  },
  deleteSettore: async (settoreId) => {
    const response = await api.delete(`/settori-spesa/${settoreId}`);
    return response.data;
  },
};

// Users API (admin only)
export const usersApi = {
  getAll: async () => {
    const response = await api.get('/users');
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/users', data);
    return response.data;
  },
  update: async (userId, data) => {
    const response = await api.put(`/users/${userId}`, data);
    return response.data;
  },
  updateRole: async (userId, ruolo) => {
    const response = await api.put(`/users/${userId}/role?ruolo=${ruolo}`);
    return response.data;
  },
  updateMemberships: async (userId, locali) => {
    const response = await api.put(`/users/${userId}/memberships`, { locali });
    return response.data;
  },
  resetPassword: async (userId) => {
    const response = await api.post(`/users/${userId}/reset-password`);
    return response.data;
  },
  remove: async (userId) => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  },
  getRoles: async () => {
    const response = await api.get('/roles');
    return response.data;
  },
};

// Uscite Cassa API
export const usciteApi = {
  getAll: async (skip = 0, limit = 100, dataInizio = null, dataFine = null) => {
    let url = `/uscite?skip=${skip}&limit=${limit}`;
    if (dataInizio) url += `&data_inizio=${dataInizio}`;
    if (dataFine) url += `&data_fine=${dataFine}`;
    const response = await api.get(url);
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/uscite/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/uscite', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/uscite/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/uscite/${id}`);
    return response.data;
  },
  getCategories: async () => {
    const response = await api.get('/uscite/categorie/list');
    return response.data;
  },
};

// Controllo di Gestione (venduto Cassa in Cloud)
export const vendutoApi = {
  getStato: async () => (await api.get('/venduto/stato')).data,
  getConfig: async () => (await api.get('/venduto/config')).data,
  saveConfig: async (capienzaInvernale, capienzaEstiva) =>
    (await api.put('/venduto/config', {
      capienza_invernale: capienzaInvernale,
      capienza_estiva: capienzaEstiva,
    })).data,

  uploadPreview: async (files) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    const res = await api.post('/venduto/upload/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },
  getUpload: async (uploadId) => (await api.get(`/venduto/upload/${uploadId}`)).data,
  setTipoFile: async (uploadId, fileId, tipo) =>
    (await api.put(`/venduto/upload/${uploadId}/file/${fileId}/tipo`, { tipo })).data,
  consolida: async (uploadId) => (await api.post(`/venduto/upload/${uploadId}/consolida`)).data,
  scartaUpload: async (uploadId) => (await api.delete(`/venduto/upload/${uploadId}`)).data,

  getMetriche: async (di, df) =>
    (await api.get(`/venduto/metriche?data_inizio=${di}&data_fine=${df}`)).data,
  getAnalisi: async (di, df) =>
    (await api.get(`/venduto/analisi?data_inizio=${di}&data_fine=${df}`)).data,
  getClassifiche: async (di, df, servizio = 'tutti') =>
    (await api.get(`/venduto/classifiche?data_inizio=${di}&data_fine=${df}&servizio=${servizio}`)).data,
  getConfronto: async (a, b) =>
    (await api.get(
      `/venduto/confronto?a_inizio=${a.data_inizio}&a_fine=${a.data_fine}` +
      `&b_inizio=${b.data_inizio}&b_fine=${b.data_fine}`
    )).data,

  getVoci: async (di, df, q = '') =>
    (await api.get(`/venduto/voci?data_inizio=${di}&data_fine=${df}&q=${encodeURIComponent(q)}`)).data,
  query: async (body) => (await api.post('/venduto/query', body)).data,
  queryNl: async (body) => (await api.post('/venduto/query-nl', body)).data,

  getPanieri: async () => (await api.get('/venduto/panieri')).data,
  createPaniere: async (body) => (await api.post('/venduto/panieri', body)).data,
  deletePaniere: async (id) => (await api.delete(`/venduto/panieri/${id}`)).data,

  downloadBackup: async () =>
    (await api.get('/venduto/backup.json', { responseType: 'blob' })).data,
  restoreBackup: async (archivio) =>
    (await api.post('/venduto/backup/restore', { archivio })).data,
  restoreBackupFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post('/venduto/backup/restore-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },
  inspectBackupFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post('/venduto/backup/inspect', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },

  inviaReportMensile: async (anno, mese) =>
    (await api.post(`/venduto/report-mensile?anno=${anno}&mese=${mese}`)).data,
};

// Acquisti e Costi (fatture passive FatturaPA)
export const acquistiApi = {
  getStato: async () => (await api.get('/acquisti/stato')).data,
  importa: async (files) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    const res = await api.post('/acquisti/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
    });
    return res.data;
  },
  getPrezzario: async (q = '', fornitoreId = '', limit = 40, soloDaClassificare = false) =>
    (await api.get(
      `/acquisti/prezzario?q=${encodeURIComponent(q)}&fornitore_id=${fornitoreId}&limit=${limit}` +
      `&solo_da_classificare=${soloDaClassificare}`
    )).data,
  ricerca: async (q) => (await api.get(`/acquisti/ricerca?q=${encodeURIComponent(q)}`)).data,
  getProdotto: async (id) => (await api.get(`/acquisti/prodotti/${id}`)).data,
  getFornitori: async (dataInizio = '', dataFine = '') =>
    (await api.get(`/acquisti/fornitori?data_inizio=${dataInizio}&data_fine=${dataFine}`)).data,
  getFornitore: async (id, dataInizio, dataFine) =>
    (await api.get(`/acquisti/fornitori/${id}?data_inizio=${dataInizio}&data_fine=${dataFine}`)).data,
  getDashboard: async (dataInizio, dataFine) =>
    (await api.get(`/acquisti/dashboard?data_inizio=${dataInizio}&data_fine=${dataFine}`)).data,
  getAndamentoMensile: async (da = '', a = '') =>
    (await api.get(`/acquisti/andamento-mensile?da=${da}&a=${a}`)).data,
  setCategoriaFornitore: async (id, categoria) =>
    (await api.put(`/acquisti/fornitori/${id}/categoria`, { categoria })).data,
  setCategoriaDocumento: async (id, categoria) =>
    (await api.put(`/acquisti/documenti/${id}/categoria`, { categoria })).data,
  setCategoriaProdotto: async (id, categoria, memorizza_regola = true) =>
    (await api.put(`/acquisti/prodotti/${id}/categoria`, { categoria, memorizza_regola })).data,
  setCategoriaProdottiMultipli: async (prodotto_ids, categoria, memorizza_regola = true) =>
    (await api.put('/acquisti/prodotti/categoria-multipla', {
      prodotto_ids, categoria, memorizza_regola,
    })).data,
  ricalcolaCategorie: async () => (await api.post('/acquisti/categorie/ricalcola')).data,
  getDocumenti: async ({ q = '', fornitore_id = '', data_inizio = '', data_fine = '', limit = 50 } = {}) =>
    (await api.get(
      `/acquisti/documenti?q=${encodeURIComponent(q)}&fornitore_id=${fornitore_id}` +
      `&data_inizio=${data_inizio}&data_fine=${data_fine}&limit=${limit}`
    )).data,
  getDocumento: async (id) => (await api.get(`/acquisti/documenti/${id}`)).data,
  downloadXml: async (id) =>
    (await api.get(`/acquisti/documenti/${id}/xml`, { responseType: 'blob' })).data,
  getCostiFissi: async (dataInizio, dataFine) =>
    (await api.get(`/acquisti/costi-fissi?data_inizio=${dataInizio}&data_fine=${dataFine}`)).data,
  creaCostoFisso: async (dati) => (await api.post('/acquisti/costi-fissi', dati)).data,
  aggiornaCostoFisso: async (id, dati) => (await api.put(`/acquisti/costi-fissi/${id}`, dati)).data,
  eliminaCostoFisso: async (id) => (await api.delete(`/acquisti/costi-fissi/${id}`)).data,
  escludiMeseCostoFisso: async (id, mese, valore = true) =>
    (await api.post(`/acquisti/costi-fissi/${id}/escludi-mese`, { mese, valore })).data,
  ignoraDoppioneCostoFisso: async (id, mese, valore = true) =>
    (await api.post(`/acquisti/costi-fissi/${id}/ignora-doppione`, { mese, valore })).data,
  getBancari: async () => (await api.get('/acquisti/bancari')).data,
  analizzaBancari: async (file) => {
    const form = new FormData();
    form.append('file', file);
    return (await api.post('/acquisti/bancari/analizza', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },
  confermaBancari: async (uploadId) =>
    (await api.post('/acquisti/bancari/conferma', { upload_id: uploadId })).data,
  eliminaBancari: async (anno, mese) =>
    (await api.delete(`/acquisti/bancari/${anno}/${mese}`)).data,
  exportAnalisi: async () => {
    const nomi = [];
    let periodo = '';
    for (const formato of ['json', 'markdown']) {
      const res = await api.get(`/export/analisi?formato=${formato}`, { responseType: 'blob' });
      const intestazione = res.headers['content-disposition'] || '';
      const nome = (intestazione.match(/filename="?([^"]+)"?/) || [])[1]
        || `export-analisi.${formato === 'json' ? 'json' : 'md'}`;
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = nome;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      nomi.push(nome.endsWith('.md') ? 'Markdown' : 'JSON');
      periodo = (nome.match(/(\d{4}-\d{2})_(\d{4}-\d{2})/) || []).slice(1, 3).join(' → ') || periodo;
    }
    return { nomi, periodo };
  },
};

// Assistente AI (ChatGPT) su venduto, acquisti, costi e personale
export const aiApi = {
  getStato: async () => (await api.get('/ai/stato')).data,
  chiedi: async (domanda, sessionId = null) =>
    (await api.post('/ai/assistente', { domanda, session_id: sessionId }, { timeout: 180000 })).data,
  getSessioni: async () => (await api.get('/ai/sessioni')).data,
  getMessaggi: async (sessionId) => (await api.get(`/ai/sessioni/${sessionId}`)).data,
  eliminaSessione: async (sessionId) => (await api.delete(`/ai/sessioni/${sessionId}`)).data,
  getCommento: async (anno = null, mese = null) => {
    const q = anno && mese ? `?anno=${anno}&mese=${mese}` : '';
    return (await api.get(`/ai/commento-mensile${q}`)).data;
  },
  generaCommento: async (anno, mese) =>
    (await api.post(`/ai/commento-mensile?anno=${anno}&mese=${mese}`, {}, { timeout: 240000 })).data,
  proponiCategorie: async (limite = 40) =>
    (await api.post(`/ai/categorie/proponi?limite=${limite}`, {}, { timeout: 180000 })).data,
};

// Promemoria mensile del controllo di gestione
export const promemoriaApi = {
  lista: async (anno = null) =>
    (await api.get(`/promemoria${anno ? `?anno=${anno}` : ''}`)).data,
  spunta: async (mese, chiave, fatto) =>
    (await api.post(`/promemoria/${mese}/spunta`, { chiave, fatto })).data,
};

// Costo del personale (report libro paga)
export const personaleApi = {  lista: async () => (await api.get('/personale')).data,
  analizza: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post('/personale/analizza', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return res.data;
  },
  conferma: async (body) => (await api.post('/personale/conferma', body)).data,
  serie: async () => (await api.get('/personale/serie')).data,
  exportXlsx: async () => {
    const res = await api.get('/personale/export.xlsx', { responseType: 'blob' });
    const intestazione = res.headers['content-disposition'] || '';
    const nome = (intestazione.match(/filename="?([^"]+)"?/) || [])[1] || 'costo-personale.xlsx';
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = nome;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return nome;
  },
  elimina: async (id) => (await api.delete(`/personale/${id}`)).data,
  pdf: async (id) => (await api.get(`/personale/${id}/pdf`, { responseType: 'blob' })).data,
};

export default api;
